import { ArrowRight, CircleDot, DoorOpen, LockKeyhole, Mail, Settings, ShieldCheck, Sparkles, UsersRound, Volume2, VolumeX, X } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { useAuth } from '../auth';
import { useMusic, useMusicScene } from '../music/MusicProvider';

type TitlePanel = 'title' | 'auth' | 'options';

export function AuthPage() {
  useMusicScene('title');
  const { enabled: musicEnabled, setEnabled: setMusicEnabled, playEffect } = useMusic();
  const { signIn, signUp, configured } = useAuth();
  const [panel, setPanel] = useState<TitlePanel>('title');
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [reducedMotion, setReducedMotion] = useState(() => localStorage.getItem('inazuma-title-reduced-motion') === '1');
  const [exitNotice, setExitNotice] = useState('');

  useEffect(() => {
    document.documentElement.classList.toggle('title-reduced-motion', reducedMotion);
    localStorage.setItem('inazuma-title-reduced-motion', reducedMotion ? '1' : '0');
  }, [reducedMotion]);

  const openAuth = (nextMode: 'signin' | 'signup') => {
    setMode(nextMode); setError(''); setMessage(''); setPanel('auth');
  };

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(''); setMessage('');
    if (mode === 'signup' && !displayName.trim()) {
      setError('Indica el nombre con el que quieres dirigir tu club.');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'signin') {
        await signIn(email, password);
      } else {
        const result = await signUp(email, password, displayName);
        setMessage(result.needsConfirmation
          ? 'Cuenta creada. Confirma el enlace que hemos enviado a tu correo antes de iniciar sesión.'
          : 'Cuenta creada correctamente. Preparando tu centro de carrera.');
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'No se ha podido continuar.');
    } finally { setBusy(false); }
  }

  const attemptExit = () => {
    if (window.opener) window.close();
    setExitNotice('Puedes cerrar esta pestaña cuando quieras. Tu progreso se conserva en Supabase.');
    window.setTimeout(() => setExitNotice(''), 3600);
  };

  return <main className="title-screen" onClickCapture={(event) => { if (event.target instanceof Element && event.target.closest('button')) playEffect('menuConfirm'); }}>
    <div className="title-stage">
      <div className="title-composition">
        <img className="title-art" src="/pantalla_inicio_inazuma_manager.png" alt="" aria-hidden="true" />
        <nav className="title-menu" aria-label="Menú de inicio">
          <button className="title-menu-button primary" onClick={() => openAuth('signin')}><CircleDot size={24} /><span>INICIAR SESIÓN</span></button>
          <button className="title-menu-button" onClick={() => openAuth('signup')}><UsersRound size={24} /><span>NUEVA PARTIDA</span></button>
          <button className="title-menu-button" onClick={() => setPanel('options')}><Settings size={24} /><span>OPCIONES</span></button>
          <button className="title-menu-button" onClick={attemptExit}><DoorOpen size={24} /><span>SALIR</span></button>
        </nav>
        {exitNotice && <p className="title-exit-notice">{exitNotice}</p>}
      </div>
    </div>

    {panel === 'auth' && <div className="title-overlay" role="presentation" onMouseDown={() => setPanel('title')}>
      <section className="title-auth-panel" role="dialog" aria-modal="true" aria-labelledby="title-auth-heading" onMouseDown={(event) => event.stopPropagation()}>
        <button className="title-panel-close" onClick={() => setPanel('title')} aria-label="Cerrar"><X size={20} /></button>
        <div className="title-auth-mark"><ShieldCheck size={25} /><span>ACCESO DEL ENTRENADOR</span></div>
        <h1 id="title-auth-heading">{mode === 'signin' ? 'Iniciar sesión' : 'Nueva partida'}</h1>
        <p>{mode === 'signin' ? 'Vuelve al banquillo y continúa tu carrera.' : 'Crea tu cuenta para fundar tu propio club.'}</p>
        {!configured && <div className="title-config-warning"><LockKeyhole size={18} /><span><b>Supabase no está conectado</b><small>Completa <code>web/.env.local</code> y reinicia el servidor.</small></span></div>}
        <form onSubmit={submit}>
          {mode === 'signup' && <label className="title-auth-field"><span>ENTRENADOR</span><input autoComplete="name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} placeholder="Tu nombre de entrenador" required /></label>}
          <label className="title-auth-field"><span><Mail size={15} /> EMAIL</span><input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="tu@email.com" required /></label>
          <label className="title-auth-field"><span><LockKeyhole size={15} /> CONTRASEÑA</span><input type="password" autoComplete={mode === 'signin' ? 'current-password' : 'new-password'} value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Mínimo 6 caracteres" minLength={6} required /></label>
          {error && <p className="title-auth-error">{error}</p>}{message && <p className="title-auth-success">{message}</p>}
          <button className="title-auth-submit" disabled={busy || !configured}>{busy ? 'Conectando…' : <>{mode === 'signin' ? 'ENTRAR AL MANAGER' : 'CREAR CUENTA'} <ArrowRight size={18} /></>}</button>
        </form>
        <button className="title-auth-switch" type="button" onClick={() => { setMode((current) => current === 'signin' ? 'signup' : 'signin'); setError(''); setMessage(''); }}>{mode === 'signin' ? '¿No tienes cuenta? Crea una nueva partida' : '¿Ya tienes cuenta? Iniciar sesión'}</button>
      </section>
    </div>}

    {panel === 'options' && <div className="title-overlay" role="presentation" onMouseDown={() => setPanel('title')}>
      <section className="title-options-panel" role="dialog" aria-modal="true" aria-labelledby="title-options-heading" onMouseDown={(event) => event.stopPropagation()}>
        <button className="title-panel-close" onClick={() => setPanel('title')} aria-label="Cerrar"><X size={20} /></button>
        <span className="eyebrow"><Sparkles size={14} /> OPCIONES DE INICIO</span><h1 id="title-options-heading">Ajustes de inicio</h1><p>Estos ajustes se guardan en este dispositivo.</p>
        <button className={`title-option-toggle ${musicEnabled ? 'active' : ''}`} onClick={() => setMusicEnabled(!musicEnabled)}><span><b>{musicEnabled ? 'Música activada' : 'Música silenciada'}</b><small>Activa la banda sonora y sus transiciones.</small></span><i>{musicEnabled ? <Volume2 size={15} /> : <VolumeX size={15} />}</i></button>
        <button className={`title-option-toggle ${reducedMotion ? 'active' : ''}`} onClick={() => setReducedMotion((value) => !value)}><span><b>Reducir movimiento</b><small>Desactiva las animaciones decorativas de la portada.</small></span><i>{reducedMotion ? 'ACTIVO' : 'NORMAL'}</i></button>
        <button className="title-options-return" onClick={() => setPanel('title')}>Volver al título</button>
      </section>
    </div>}
  </main>;
}
